import { useCallback, useEffect, useRef, useState } from 'react';

import { startLatencyProbe } from '../../../services/latencyProbe';
import {
  getRecentNetworkSamples,
  getRecentRequestSamples,
  LatencySample,
  subscribeToLatencySamples,
} from '../../../services/networkLatencyObserver';
import { startResourceTimingObserver } from '../../../services/resourceTimingObserver';
import { adaptNetworkSignalsToContext } from '../adapters/adaptNetworkSignalsToContext';
import { computeDegradedCandidate } from '../helpers/computeDegradedCandidate';
import { getNetworkConnection } from '../helpers/getNetworkConnection';
import { readNetworkSignals } from '../helpers/readNetworkSignals';
import {
  ENTER_DEGRADED_DELAY_MS,
  EXIT_DEGRADED_DELAY_MS,
  LATENCY_WINDOW_SIZE,
} from '../NetworkStatusProvider.constants';
import {
  NETWORK_STATUS,
  NetworkQualityContext,
  NetworkStatus,
} from '../NetworkStatusProvider.types';

export interface UseNetworkQualityArgs {
  isEnabled: boolean;
}

interface NetworkSnapshot {
  networkContext: NetworkQualityContext;
  requestSamples: LatencySample[];
  networkSamples: LatencySample[];
}

export interface UseNetworkQualityReturnValue extends NetworkSnapshot {
  networkStatus: NetworkStatus;
}

const EMPTY_NETWORK_SNAPSHOT: NetworkSnapshot = {
  networkContext: {},
  requestSamples: [],
  networkSamples: [],
};

export const useNetworkQuality = ({
  isEnabled,
}: UseNetworkQualityArgs): UseNetworkQualityReturnValue => {
  const [networkState, setNetworkState] =
    useState<UseNetworkQualityReturnValue>({
      networkStatus: NETWORK_STATUS.GOOD,
      ...EMPTY_NETWORK_SNAPSHOT,
    });

  const latestNetworkSnapshotRef = useRef<NetworkSnapshot>(
    EMPTY_NETWORK_SNAPSHOT,
  );

  const statusRef = useRef<NetworkStatus>(networkState.networkStatus);

  statusRef.current = networkState.networkStatus;

  const setNetworkStatus = useCallback((networkStatus: NetworkStatus) => {
    const networkSnapshot = latestNetworkSnapshotRef.current;

    setNetworkState((previousState) => {
      if (previousState.networkStatus === networkStatus) {
        return previousState;
      }

      return { networkStatus, ...networkSnapshot };
    });
  }, []);

  const enterTimerRef = useRef<number | undefined>(undefined);
  const exitTimerRef = useRef<number | undefined>(undefined);

  const clearEnterTimer = useCallback(() => {
    if (enterTimerRef.current !== undefined) {
      window.clearTimeout(enterTimerRef.current);
      enterTimerRef.current = undefined;
    }
  }, []);

  const clearExitTimer = useCallback(() => {
    if (exitTimerRef.current !== undefined) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = undefined;
    }
  }, []);

  const evaluate = useCallback(() => {
    const signals = readNetworkSignals();
    const degradedTrigger = computeDegradedCandidate(signals);

    latestNetworkSnapshotRef.current = {
      networkContext: adaptNetworkSignalsToContext({
        signals,
        degradedTrigger,
      }),
      requestSamples: getRecentRequestSamples(LATENCY_WINDOW_SIZE),
      networkSamples: getRecentNetworkSamples(LATENCY_WINDOW_SIZE),
    };

    if (!signals.isOnline) {
      clearEnterTimer();
      clearExitTimer();
      setNetworkStatus(NETWORK_STATUS.OFFLINE);

      return;
    }

    const isDegradedCandidate = degradedTrigger !== undefined;
    const currentStatus = statusRef.current;

    if (currentStatus === NETWORK_STATUS.OFFLINE) {
      clearEnterTimer();
      clearExitTimer();
      setNetworkStatus(
        isDegradedCandidate ? NETWORK_STATUS.DEGRADED : NETWORK_STATUS.GOOD,
      );

      return;
    }

    if (isDegradedCandidate) {
      clearExitTimer();

      if (
        currentStatus === NETWORK_STATUS.GOOD &&
        enterTimerRef.current === undefined
      ) {
        enterTimerRef.current = window.setTimeout(() => {
          enterTimerRef.current = undefined;
          setNetworkStatus(NETWORK_STATUS.DEGRADED);
        }, ENTER_DEGRADED_DELAY_MS);
      }

      return;
    }

    clearEnterTimer();

    if (
      currentStatus === NETWORK_STATUS.DEGRADED &&
      exitTimerRef.current === undefined
    ) {
      exitTimerRef.current = window.setTimeout(() => {
        exitTimerRef.current = undefined;
        setNetworkStatus(NETWORK_STATUS.GOOD);
      }, EXIT_DEGRADED_DELAY_MS);
    }
  }, [clearEnterTimer, clearExitTimer, setNetworkStatus]);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    evaluate();

    const connection = getNetworkConnection();

    window.addEventListener('online', evaluate);
    window.addEventListener('offline', evaluate);
    connection?.addEventListener?.('change', evaluate);
    const unsubscribe = subscribeToLatencySamples(evaluate);
    const disconnectResourceTimingObserver = startResourceTimingObserver();
    const stopLatencyProbe = startLatencyProbe();

    return () => {
      window.removeEventListener('online', evaluate);
      window.removeEventListener('offline', evaluate);
      connection?.removeEventListener?.('change', evaluate);
      unsubscribe();
      disconnectResourceTimingObserver();
      stopLatencyProbe();
      clearEnterTimer();
      clearExitTimer();
    };
  }, [isEnabled, evaluate, clearEnterTimer, clearExitTimer]);

  return networkState;
};
